import { useEffect, useRef, useState } from 'react';

export default function Track({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    let stop = false;
    async function load() {
      try {
        const res = await fetch('/api/track/' + encodeURIComponent(token));
        const d = await res.json();
        if (stop) return;
        if (!res.ok) { setError(d.error || 'No disponible'); setData(null); return; }
        setData(d);
        setError(null);
      } catch (e) { setError('Error de red'); }
    }
    load();
    const t = setInterval(load, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [token]);

  useEffect(() => {
    if (!data || !data.position) return;
    if (!mapRef.current) {
      const map = L.map('track-map').setView([data.position.latitude, data.position.longitude], 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
      mapRef.current = map;
    }
    const latlng = [data.position.latitude, data.position.longitude];
    if (markerRef.current) markerRef.current.setLatLng(latlng);
    else markerRef.current = L.marker(latlng).addTo(mapRef.current).bindPopup(data.tractor || 'Unidad');
  }, [data]);

  useEffect(() => {
    if (!data || !data.tempHistory || !data.tempHistory.length) return;
    const ctx = document.getElementById('track-chart');
    if (!ctx) return;
    if (window.__trackChart) window.__trackChart.destroy();
    const light = document.documentElement.dataset.theme === 'light';
    const tc = light ? '#1a2233' : '#e6ebf5';
    const gc = light ? '#d9e0ea' : '#263047';
    window.__trackChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.tempHistory.map((p) => (p.t || '').slice(11, 16)),
        datasets: [
          { label: 'Temperatura (°C)', data: data.tempHistory.map((p) => p.temp), borderColor: '#f87171', backgroundColor: 'rgba(248,113,113,0.15)', fill: true, tension: 0.3 },
          { label: 'Humedad (%)', data: data.tempHistory.map((p) => p.humidity), borderColor: '#38bdf8', fill: false, tension: 0.3, yAxisID: 'y2' },
        ],
      },
      options: {
        responsive: true,
        plugins: { legend: { labels: { color: tc } } },
        scales: {
          x: { ticks: { color: tc }, grid: { color: gc } },
          y: { ticks: { color: tc }, grid: { color: gc } },
          y2: { position: 'right', ticks: { color: tc }, grid: { drawOnChartArea: false } },
        },
      },
    });
  }, [data]);

  function tempOutOfRange() {
    if (!data || data.latestSensors.temp == null) return false;
    const t = data.latestSensors.temp;
    if (data.temp_min != null && t < data.temp_min) return true;
    if (data.temp_max != null && t > data.temp_max) return true;
    return false;
  }

  return (
    <div className="track-public">
      <header className="topbar">
        <div className="brand">
          <span className="logo-text">Buyubampo</span>
        </div>
        <div className="badges">
          <span className="badge ok">Monitoreo de viaje</span>
        </div>
      </header>

      {error && <div className="card"><div className="empty">{error}</div></div>}

      {data && (
        <>
          <section className="stats">
            <div className="card stat"><div className="stat-value">{data.tractor || '—'}</div><div className="stat-label">Tractocamión</div></div>
            <div className="card stat"><div className="stat-value">{data.box || '—'}</div><div className="stat-label">Caja</div></div>
            <div className="card stat"><div className="stat-value">{data.latestSensors.temp != null ? data.latestSensors.temp + ' °C' : '—'}</div><div className="stat-label">Temperatura actual</div></div>
            <div className="card stat"><div className="stat-value">{data.latestSensors.door != null ? (data.latestSensors.door === true || data.latestSensors.door === 'true' || data.latestSensors.door === 1 ? 'Abierta' : 'Cerrada') : '—'}</div><div className="stat-label">Puerta</div></div>
          </section>

          {tempOutOfRange() && (
            <div className="card" style={{ borderColor: '#f87171', marginBottom: 16 }}>
              <div className="reg-msg error">Alerta: temperatura fuera del rango permitido ({data.temp_min}–{data.temp_max} °C)</div>
            </div>
          )}

          <section className="card panel">
            <h2>Ubicación en tiempo real</h2>
            <div id="track-map" className="track-map" />
          </section>

          <section className="card panel">
            <h2>Temperatura y humedad</h2>
            {data.tempHistory.length === 0 && <div className="empty">Aún sin datos de sensores</div>}
            <div style={{ height: 260 }}><canvas id="track-chart" /></div>
          </section>

          <section className="card panel">
            <h2>Detalles del viaje</h2>
            <table>
              <tbody>
                <tr><td className="dim">Cliente</td><td>{data.client_name || '—'}</td></tr>
                <tr><td className="dim">Rango de temperatura</td><td>{data.temp_min ?? '—'} – {data.temp_max ?? '—'} °C</td></tr>
                <tr><td className="dim">Inicio</td><td>{data.starts_at ? new Date(data.starts_at).toLocaleString('es-MX', { hour12: false }) : '—'}</td></tr>
                <tr><td className="dim">Velocidad</td><td>{data.position?.speedKmh ?? '—'} km/h</td></tr>
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
